import React, { useMemo } from "react";
import useRefCallback from "use-ref-callback";
import styles from "../../../styles/conversations-tabs.module.css";
import TabsNew, { TTabsItem } from "@atlas/uicorn/lib/Display/TabsNew";
import { TOptionsListItem } from "@atlas/uicorn/lib/Control/OptionsList";
import { AppSettings } from "../../../lib/get-app-settings";
import LoginAlert from "./login-alert";
import getBaseUrl from "../../../lib/get-base-url";
import HeaderActions from "../../../components/customer-portal/conversations-list/header-actions";
import ConversationsList from "../../../components/customer-portal/conversations-list/conversations-list";
import { useRouter } from "next/router";
import useCustomerPortalContext, {
  ConversationTab,
} from "../customer-portal-context";
import t from "../../../texts/t";
import cn from "classnames";

const ConversationsTabs = function ConversationsTabs(
  props: TConversationsTabsProps
) {
  const { className, appSettings, hideTabs } = props;

  const baseUrl = useMemo(() => getBaseUrl(appSettings), [appSettings]);

  const router = useRouter();
  const { customer, totals } = useCustomerPortalContext();

  const tabs: TTabsItem[] = useMemo(
    () => [
      {
        key: ConversationTab.customer,
        label: t("My Tickets", appSettings.language),
        description: totals[ConversationTab.customer].toString(),
      },
      ...(appSettings?.enableAccountLevelSupportPortal
        ? [
            {
              key: ConversationTab.account,
              label: t("Company tickets", appSettings.language),
              description: totals[ConversationTab.account].toString(),
            },
          ]
        : []),
    ],
    [totals, appSettings]
  );

  const selectedTab = useMemo(
    () =>
      (router.query?.tab?.toString() ||
        ConversationTab.customer) as ConversationTab,
    [router.query]
  );

  const handleTabSelect = useRefCallback(({ key: tab }: TOptionsListItem) => {
    const { companySlug, ...oldQuery } = router.query;

    router.replace({
      pathname: `${baseUrl}/customer-portal`,
      query: { ...oldQuery, tab },
    });
  });

  const isVisitor = useMemo(() => !customer || customer.isVisitor, [customer]);

  return (
    <div className={cn(styles.conversationTabs, className)}>
      {appSettings.supportPortalLoginUrl &&
        (!customer || customer.isVisitor) && (
          <LoginAlert
            language={appSettings.language}
            loginUrl={appSettings.supportPortalLoginUrl}
          />
        )}

      <div className={styles.tabsRow}>
        {!hideTabs && (
          <TabsNew
            className={styles.tabs}
            items={tabs}
            cursor={selectedTab}
            onChoose={handleTabSelect}
          />
        )}
        <div className={styles.spacer} />
        <HeaderActions
          appSettings={appSettings}
          tab={selectedTab}
          language={appSettings.language}
          isVisitor={isVisitor}
        />
      </div>

      <ConversationsList
        tab={selectedTab}
        baseUrl={baseUrl}
        language={appSettings.language}
      />
    </div>
  );
};

export default ConversationsTabs;

type TConversationsTabsProps = {
  className?: string;
  appSettings: AppSettings;
  hideTabs?: boolean;
};
